// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LinesHeader, { LinesHeaderStyles } from "@conklin/App/Components/Cart/LinesHeader";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@conklin/Overrides/Components/CartLineCardExpanded";
import CartLinesListHeader, { CartLinesListHeaderStyles } from "@conklin/Overrides/Components/CartLinesListHeader";
import { parseBoolean } from "@conklin/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import CartLineCardCondensed, {
    CartLineCardCondensedStyles,
} from "@insite/content-library/Components/CartLineCardCondensed";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    editable: boolean;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    extendedStyles?: CartLinesListStyles;
    // XNG-Changes
    showEditCart?: boolean;
    showAddToList?: boolean;
    showInventory?: boolean;
    includeActions?: boolean;
    linesHeaderStyles?: LinesHeaderStyles;
}

export interface CartLinesListStyles {
    wrapper?: InjectableCss;
    header?: CartLinesListHeaderStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    pagination?: PaginationPresentationProps;
}

export const orderLinesListStyles: CartLinesListStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
};

const CartLinesList: FC<OwnProps> = ({
    cart,
    promotions,
    isCondensed,
    hideCondensedSelector,
    onChangeIsCondensed,
    editable,
    showSavingsAmount,
    showSavingsPercent,
    extendedStyles,
    // XNG-Changes
    showEditCart = true,
    showAddToList = true,
    showInventory = true,
    includeActions = true,
    linesHeaderStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));
    // XNG-Changes
    const { isAuthenticated } = useAppSelector(state => {
        return {
            isAuthenticated: xIsAuthenticated(state),
        };
    });

    if (!cart.cartLines) {
        return null;
    }

    // XNG-Changes
    const cartLines =
        cart.status === "Cart"
            ? cart.cartLines.filter(
                  cartline =>
                      !cartline.quoteRequired &&
                      !(!isAuthenticated && parseBoolean(cartline.properties.authRequiredForPurchase)) &&
                      !parseBoolean(cartline.properties.isQuote),
              )
            : cart.cartLines;

    const CartLineComponent = isCondensed ? CartLineCardCondensed : CartLineCardExpanded;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`cardLinesList_${cart.id}`}>
            <CartLinesListHeader
                productsCount={cartLines.length}
                isCondensed={isCondensed}
                hideCondensedSelector={hideCondensedSelector}
                onChangeIsCondensed={onChangeIsCondensed}
                extendedStyles={styles.header}
                showEditCart={showEditCart}
            />
            {/* XNG-Changes */}
            {!showInventory ? (
                <LinesHeader
                    rightTitles={["", "QTY", "Total"]}
                    includeActions={includeActions}
                    extendedStyles={linesHeaderStyles}
                />
            ) : (
                <LinesHeader includeActions={includeActions} extendedStyles={linesHeaderStyles} />
            )}
            {cartLines!.map(cartLine => {
                const cartLinePromotions = promotions?.filter(promotion => promotion.orderLineId === cartLine.id);
                return (
                    <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                        <CartLineComponent
                            cart={cart}
                            promotions={cartLinePromotions}
                            editable={editable}
                            showSavingsAmount={showSavingsAmount}
                            showSavingsPercent={showSavingsPercent}
                            // XNG-Changes
                            extendedStyles={
                                !editable ? (isCondensed ? styles.cardCondensed : styles.cardExpanded) : undefined
                            }
                            showAddToList={showAddToList}
                            includeActions={includeActions}
                        />
                    </CartLineContext.Provider>
                );
            })}
        </StyledWrapper>
    );
};

export default CartLinesList;
